import { Divider, Page, Spinner } from "@whitespace/whitespace-ui";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { useStateMachine } from "little-state-machine";
import { useCallback, useEffect, useState } from "react";
import {
  Breadcrumb, Button, Card, Col, Row
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { formatDateWithTime } from "helpers/DateUtils";
import { IsNonSiteUser } from "modules/CheckPermissions";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import {
  AllowedToCompleteStatuses,
  WorkOrderEndOfLifeStatusesProvider,
  WorkOrderEndOfLifeStatusesVehicle,
  WorkOrderStatusEnum
} from "modules/WorkOrderStatusEnum";
import IWorkOrderViewModel from "interfaces/IWorkOrderViewModel";
import IGetWorkOrderResponseModel from "interfaces/Response/IGetWorkOrderResponseModel";
import { updateCompleteWorkOrderRequest } from "actions/updateCompleteWorkOrderRequest";
import { updateCompleteWorkOrderPageOrigination } from "actions/updateCompleteWorkOrderPageOrigination";
import { SourceEnum } from "modules/sourceEnum";
import { mapWorkOrderResponseToWorkOrderViewModel } from "./DataMappers/ViewWorkOrderMapper";

import "./ViewWorkOrder.scss";

const ViewWorkOrder = () => {
  const navigate = useNavigate();
  const { state } = useStateMachine();
  const {
    id, source, sourceType, sourceId
  } = useParams();
  const { actions } = useStateMachine({
    updateCompleteWorkOrderRequest,
    updateCompleteWorkOrderPageOrigination
  });

  const {
    isSending, errors, data, sendRequest
  } = useAmplifyREST();

  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [workOrder, setWorkOrder] = useState<IWorkOrderViewModel>();

  useEffect(() => {
    sendRequest(`workorders/${id}`, "GET");
  }, [sendRequest]);

  useEffect(() => {
    if (data) {
      const workOrderViewModel = mapWorkOrderResponseToWorkOrderViewModel(
        (data as IGetWorkOrderResponseModel).workOrder
      );
      setWorkOrder(workOrderViewModel);
    }
  }, [data]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const completeWorkOrderOrigin = (): void => {
    actions.updateCompleteWorkOrderPageOrigination(
      state.viewWorkOrderPageOrigination
    );
  };

  const navigateToModifyWorkOrder = ():void => {
    switch (state.viewWorkOrderPageOrigination) {
      case RoutePathsEnum.Map:
        navigate(`${RoutePathsEnum.WorkOrderModify}/${id}/${source}/${sourceType}/${sourceId}`);
        break;
      case RoutePathsEnum.WorkOrders:
      case RoutePathsEnum.Containers:
      default:
        navigate(`${RoutePathsEnum.WorkOrderModify}/${id}`);
        break;
    }
  };

  const navigateToCompleteWorkOrder = (): void => {
    actions.updateCompleteWorkOrderRequest(workOrder);
    completeWorkOrderOrigin();
    switch (state.viewWorkOrderPageOrigination) {
      case RoutePathsEnum.Map:
        navigate(`${RoutePathsEnum.WorkOrderComplete}/${id}/${source}/${sourceType}/${sourceId}`);
        break;
      case RoutePathsEnum.WorkOrders:
      case RoutePathsEnum.Containers:
      default:
        navigate(`${RoutePathsEnum.WorkOrderComplete}/${id}`);
        break;
    }
  };

  const renderCompleteButton = (): any => {
    let completeButton = <></>;

    if (!workOrder || (!IsNonSiteUser(state.userRole!) && workOrder.collectorIsVehicle)) return completeButton;

    if (
      workOrder.status === WorkOrderStatusEnum.TempDropped
      || !AllowedToCompleteStatuses.includes(workOrder.status)
    ) {
      return completeButton;
    }

    const isWorkOrderCompleted = workOrder.collectorIsVehicle
      ? WorkOrderEndOfLifeStatusesVehicle.includes(workOrder.status!)
      : WorkOrderEndOfLifeStatusesProvider.includes(workOrder.status!);

    if (isWorkOrderCompleted) return completeButton;

    completeButton = (
      <>
        <Button
          variant="primary"
          onClick={navigateToCompleteWorkOrder}
          className="me-2"
          data-testid="buttonComplete"
          aria-label="Complete Work Order"
        >
          Complete
        </Button>
      </>
    );
    return completeButton;
  };

  const renderModifyButton = (): any => {
    let modifyButton = <></>;

    if (!workOrder || !IsNonSiteUser(state.userRole!)) return modifyButton;

    const isWorkOrderCompleted = workOrder.collectorIsVehicle
      ? WorkOrderEndOfLifeStatusesVehicle.includes(workOrder.status!)
      : WorkOrderEndOfLifeStatusesProvider.includes(workOrder.status!);

    if (isWorkOrderCompleted) return modifyButton;

    modifyButton = (
      <>
        <Button
          variant="primary"
          onClick={navigateToModifyWorkOrder}
          className="me-2"
          data-testid="buttonModify"
          aria-label="Modify Work Order"
        >
          Modify
        </Button>
      </>
    );

    return modifyButton;
  };

  const renderForTempDropped = (): any => {
    let display;
    if (workOrder && workOrder.status === WorkOrderStatusEnum.TempDropped) {
      let location = workOrder.tempDropHwrcSiteName;

      if (workOrder.tempDropRegionName) {
        location = `${workOrder.tempDropHwrcSiteName} - ${workOrder.tempDropRegionName}`;
      }

      display = (
        <>
          <div
            className="d-table-row"
            data-testid="Location-container-temporary-drop"
          >
            <div className="d-table-cell pb-2">
              Location of container’s temporary drop:
            </div>
            <div className="d-table-cell pb-2">{location}</div>
          </div>
        </>
      );
    }

    return display;
  };

  const renderOriginBreadcrumb = (): any => {
    let dataTestId: string = "";
    let linkTo: string = "";
    let label: string = "";
    if (source === SourceEnum.Map && sourceId !== undefined) {
      dataTestId = "breadcrum-map";
      linkTo = `${RoutePathsEnum.Map}/${SourceEnum.WorkOrders}/${sourceType}/${sourceId}`;
      label = "Map";
    } else {
      dataTestId = state.viewWorkOrderPageOrigination === RoutePathsEnum.Containers
        ? "breadcrumb-container-status"
        : "breadcrumb-work-orders";
      linkTo = state.viewWorkOrderPageOrigination ?? RoutePathsEnum.WorkOrders;
      label = state.viewWorkOrderPageOrigination === RoutePathsEnum.Containers
        ? "Container Status"
        : "Work Orders";
    }

    return (
      <Breadcrumb className="left-margin-large no-wrap">
        <Breadcrumb.Item
          data-testid={dataTestId}
          linkAs={Link}
          linkProps={{
            to: linkTo
          }}
          onClick={() => navigate(linkTo)}
        >
          {label}
        </Breadcrumb.Item>

        <Breadcrumb.Item data-testid="breadcrumb-view-work-order" active>
          View Work Order
        </Breadcrumb.Item>
      </Breadcrumb>
    );
  };

  const renderCancelButton = (): any => {
    let ariaLabel: string = "";
    let linkTo: string = "";
    if (source === SourceEnum.Map && sourceId !== undefined) {
      ariaLabel = "Cancel view work order and back to Map";
      linkTo = `${RoutePathsEnum.Map}/${SourceEnum.WorkOrders}/${sourceType}/${sourceId}`;
    } else {
      ariaLabel = `Cancel view work order and back to ${
        state.viewWorkOrderPageOrigination === RoutePathsEnum.Containers
          ? "containers"
          : "work orders"
      }`;

      linkTo = state.viewWorkOrderPageOrigination!;
    }

    return (
      <Button
        variant="secondary"
        onClick={() => navigate(linkTo)}
        data-testid="buttonCancel"
        aria-label={ariaLabel}
      >
        Cancel
      </Button>
    );
  };

  return (
    <Page>
      {showExceptions && errors && (
        <AmplifyError
          errors={errors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Card>
        <Card.Header>
          <h2>
            View Work Order
            <Spinner
              animation="grow"
              visible={isSending}
              spinnerText="Loading"
              variant="info"
            />
          </h2>
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
            <Col xs={12} md={6} className="d-flex">
              {workOrder && renderOriginBreadcrumb()}
            </Col>
            <Col className="d-flex justify-content-end user-filter">
              {renderModifyButton()}
              {renderCompleteButton()}
              {renderCancelButton()}
            </Col>
          </Row>
          <Divider />
          {workOrder && (
            <section className="view-work-order pt-2">
              <Row className="mt-1 ms-1">
                <Col xs={12} md={8}>
                  <div className="control-pane">
                    <div className="control-section">
                      <div className="d-table w-100 horiz-form-label form-label">
                        <div
                          className="d-table-row"
                          data-testid="workOrderReference"
                        >
                          <div className="d-table-cell pb-2">Ref:</div>
                          <div className="d-table-cell pb-2">
                            {workOrder.workOrderReference}
                          </div>
                        </div>

                        <div className="d-table-row" data-testid="hwrcSiteName">
                          <div className="d-table-cell pb-2">Site:</div>
                          <div className="d-table-cell pb-2">{`${workOrder.hwrcSiteName} - ${workOrder.regionName}`}</div>
                        </div>
                        <div className="d-table-row" data-testid="container">
                          <div className="d-table-cell pb-2">
                            Container Type:
                          </div>
                          <div className="d-table-cell pb-2">
                            {`${workOrder.containerName} - ${workOrder.containerWasteType} - ${workOrder.containerSize}`}
                          </div>
                        </div>
                        <div className="d-table-row" data-testid="allocatedTo">
                          <div className="d-table-cell pb-2">Allocated To:</div>
                          <div className="d-table-cell pb-2">
                            {workOrder.collectorName}
                          </div>
                        </div>
                        <div className="d-table-row" data-testid="status">
                          <div className="d-table-cell pb-2">Status:</div>
                          <div className="d-table-cell pb-2">
                            {workOrder.status}
                          </div>
                        </div>
                        <div className="d-table-row" data-testid="dueBy">
                          <div className="d-table-cell pb-2">Due By:</div>
                          <div className="d-table-cell pb-2">
                            {formatDateWithTime(workOrder.slaEnd)}
                          </div>
                        </div>
                        <div className="d-table-row" data-testid="disposalSite">
                          <div className="d-table-cell pb-2">
                            Disposal Site:
                          </div>
                          <div className="d-table-cell pb-2">
                            {workOrder.disposalSiteName}
                          </div>
                        </div>
                        <div className="d-table-row" data-testid="raisedBy">
                          <div className="d-table-cell pb-2">Raised By:</div>
                          <div className="d-table-cell pb-2">
                            {workOrder.raisedBy}
                          </div>
                        </div>
                        <div
                          className="d-table-row"
                          data-testid="raisedByTimeStamp"
                        >
                          <div className="d-table-cell pb-2">Raised Date:</div>
                          <div className="d-table-cell pb-2">
                            {formatDateWithTime(workOrder.raisedByTimestamp)}
                          </div>
                        </div>
                        <div className="d-table-row" data-testid="comments">
                          <div className="d-table-cell pb-2">Comments:</div>
                          <div className="d-table-cell pb-2">
                            {workOrder.comments}
                          </div>
                        </div>
                        {workOrder.updateReason && (
                          <div
                            className="d-table-row"
                            data-testid="updateReason"
                          >
                            <div className="d-table-cell pb-2">
                              Modified Reason:
                            </div>
                            <div className="d-table-cell pb-2">
                              {workOrder.updateReason}
                            </div>
                          </div>
                        )}
                        {workOrder.nonCollectionReason && (
                          <div
                            className="d-table-row"
                            data-testid="nonCollectionReason"
                          >
                            <div className="d-table-cell pb-2">
                              Reason for Non-Collection:
                            </div>
                            <div className="d-table-cell pb-2">
                              {workOrder.nonCollectionReason}
                            </div>
                          </div>
                        )}
                        {workOrder.nonDisposalReason && (
                          <div
                            className="d-table-row"
                            data-testid="nonDisposalReason"
                          >
                            <div className="d-table-cell pb-2">
                              Reason for Non-Disposal:
                            </div>
                            <div className="d-table-cell pb-2">
                              {workOrder.nonDisposalReason}
                            </div>
                          </div>
                        )}
                        {workOrder && renderForTempDropped()}
                        {workOrder.completionReason && (
                          <div
                            className="d-table-row"
                            data-testid="completionReason"
                          >
                            <div className="d-table-cell pb-2">
                              Completion Reason:
                            </div>
                            <div className="d-table-cell pb-2">
                              {workOrder.completionReason}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </section>
          )}
        </Card.Body>
      </Card>
    </Page>
  );
};

export default ViewWorkOrder;
